import { useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';
import { AntdSelect } from '../AntdSelect';
import { useAllBadgeList } from './hooks';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { Badge, OfficialBadge } from './types';

import style from './index.module.scss';
import { transformBadgeIdToValue, valueToBadgeId } from './utils';

const { Option, OptGroup } = AntdSelect;

type Props = {
  name: string;
  className?: string;
  control: Control<FieldValues, object>;
  disabled?: boolean;
  inputWidth?: number;
  placeholder?: string;
};

const BadgeOption = ({ badgeDetail }: { badgeDetail: Badge | OfficialBadge }) => {
  return (
    <div className={style.optionContainer}>
      <img className={style.badgeAvatar} src={badgeDetail.logo_thumbnail_url} alt="" />
      <span className={style.badgeName}>{badgeDetail.name}</span>
    </div>
  );
};

export const BadgeSelect = (props: Props) => {
  const { t } = useTranslation();

  const { name, control, disabled } = props;
  const { data } = useAllBadgeList();
  const badgeOption = useMemo(() => {
    return data?.badge_list?.map(i => (
      <Option key={`${i.id}`} value={`${i.id}`} label={`${i.name}`}>
        <BadgeOption badgeDetail={i} />
      </Option>
    ));
  }, [data?.badge_list]);

  const officialBadgeOption = useMemo(
    () =>
      data?.officialBadge_list?.map(i => (
        <Option key={`official_${i.id}`} value={`official_${i.id}`} label={`${i.name}`}>
          <BadgeOption badgeDetail={i} />
        </Option>
      )),
    [data?.officialBadge_list],
  );

  const emptyContent = useMemo(
    () => <div className={style.empty}>{t('BadgeSelect.1m47zc6')}</div>,
    [t],
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const value = data ? String(valueToBadgeId(field.value)) : '';
        const onChange = value => {
          field.onChange(transformBadgeIdToValue(value));
        };
        return (
          <AntdSelect
            className={style.select}
            dropdownClassName={style.dropdown}
            value={value}
            onChange={onChange}
            disabled={disabled}
            showSearch
            notFoundContent={emptyContent}
            filterOption={(input, option) =>
              (option?.props?.label as string)?.toLowerCase()?.indexOf(input.toLowerCase()) >= 0
            }>
            <OptGroup label={t('BadgeSelect.1f4rx81')}>{badgeOption}</OptGroup>
            <OptGroup label={t('BadgeSelect.1ovcenr')}>{officialBadgeOption}</OptGroup>
          </AntdSelect>
        );
      }}
    />
  );
};
